/**
 * Onboarding Tour Component
 *
 * A guided tour that helps new users learn how to use Photodrop.
 */

import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react'
import {
  TOUR_STEPS,
  shouldShowTour,
  advanceToNextStep,
  goToPreviousStep,
  completeTour,
  skipTour,
  getCurrentStep,
} from '@/lib/onboardingStore'
import { cn } from '@/lib/utils'

interface OnboardingTourProps {
  onComplete?: () => void
}

export function OnboardingTour({ onComplete }: OnboardingTourProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const currentStep = TOUR_STEPS[currentStepIndex]
  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === TOUR_STEPS.length - 1
  const isCenterPlacement = currentStep?.placement === 'center'

  // Check if tour should be shown on mount
  useEffect(() => {
    if (shouldShowTour()) {
      setIsVisible(true)
      setCurrentStepIndex(getCurrentStep())
    }
  }, [])

  // Find and highlight target element
  useEffect(() => {
    if (!isVisible || !currentStep?.targetSelector) {
      setTargetRect(null)
      return
    }

    const findTarget = () => {
      const target = document.querySelector(currentStep.targetSelector!)
      if (target) {
        const rect = target.getBoundingClientRect()
        setTargetRect(rect)
      } else {
        setTargetRect(null)
      }
    }

    findTarget()
    // Recheck on scroll/resize
    window.addEventListener('scroll', findTarget)
    window.addEventListener('resize', findTarget)

    return () => {
      window.removeEventListener('scroll', findTarget)
      window.removeEventListener('resize', findTarget)
    }
  }, [isVisible, currentStep])

  const handleNext = () => {
    if (isLastStep) {
      completeTour()
      setIsVisible(false)
      onComplete?.()
    } else {
      const nextStep = advanceToNextStep()
      setCurrentStepIndex(nextStep)
    }
  }

  const handlePrevious = () => {
    const prevStep = goToPreviousStep()
    setCurrentStepIndex(prevStep)
  }

  const handleSkip = () => {
    skipTour()
    setIsVisible(false)
    onComplete?.()
  }

  if (!isVisible || !currentStep) return null

  // Calculate tooltip position
  const getTooltipStyle = (): React.CSSProperties => {
    if (isCenterPlacement || !targetRect) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }
    }

    const arrowOffset = 12

    switch (currentStep.placement) {
      case 'bottom':
        return {
          position: 'fixed',
          top: targetRect.bottom + arrowOffset,
          left: targetRect.left + targetRect.width / 2,
          transform: 'translateX(-50%)',
        }
      case 'top':
        return {
          position: 'fixed',
          bottom: window.innerHeight - targetRect.top + arrowOffset,
          left: targetRect.left + targetRect.width / 2,
          transform: 'translateX(-50%)',
        }
      case 'left':
        return {
          position: 'fixed',
          top: targetRect.top + targetRect.height / 2,
          right: window.innerWidth - targetRect.left + arrowOffset,
          transform: 'translateY(-50%)',
        }
      case 'right':
        return {
          position: 'fixed',
          top: targetRect.top + targetRect.height / 2,
          left: targetRect.right + arrowOffset,
          transform: 'translateY(-50%)',
        }
      default:
        return {
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }
    }
  }

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[100] bg-black/60" data-tour="overlay" />

      {/* Highlight box for target element */}
      {targetRect && !isCenterPlacement && (
        <div
          className="fixed z-[101] rounded-lg ring-4 ring-primary ring-offset-2 ring-offset-background"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            backgroundColor: 'transparent',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
          }}
          data-tour="highlight"
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={cn(
          "z-[102] w-[360px] rounded-xl bg-popover text-popover-foreground border border-border shadow-2xl",
          isCenterPlacement && "max-w-md"
        )}
        style={getTooltipStyle()}
        data-tour="tooltip"
      >
        <div className="p-6">
          {/* Header with step indicator */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="text-sm text-muted-foreground">
                Step {currentStepIndex + 1} of {TOUR_STEPS.length}
              </span>
            </div>
            <button
              onClick={handleSkip}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Skip tour"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <h3 className="text-xl font-semibold mb-2">{currentStep.title}</h3>
          <p className="text-muted-foreground mb-6">{currentStep.description}</p>

          {/* Step dots */}
          <div className="flex justify-center gap-1.5 mb-6">
            {TOUR_STEPS.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "h-2 w-2 rounded-full transition-colors",
                  index === currentStepIndex ? "bg-primary" : "bg-muted"
                )}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-3">
            {!isFirstStep ? (
              <Button
                variant="outline"
                onClick={handlePrevious}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="text-muted-foreground"
              >
                Skip tour
              </Button>
            )}

            <Button onClick={handleNext} className="gap-1">
              {isLastStep ? (
                "Get Started"
              ) : (
                <>
                  Next
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

export default OnboardingTour
